import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { HeaderWithStats } from "services/MetadataServices";
import { AppIcons } from "constants/icons";

export interface ShareWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowName: string;
  ownerId: string;
  analystsList: HeaderWithStats[];
  initialSelectedIds: string[];
  isLoadingInitial?: boolean;
  isSaving?: boolean;
  onSave: (selectedAnalystIds: string[]) => void | Promise<void>;
}

const AVATAR_COLORS = ["purple.500", "green.500", "orange.400", "blue.500", "pink.500", "teal.500"];

function getAnalystInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

function getAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash + id.charCodeAt(i)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash];
}

const ShareWorkflowModal: React.FC<ShareWorkflowModalProps> = ({
  isOpen,
  onClose,
  workflowName,
  ownerId,
  analystsList,
  initialSelectedIds,
  isLoadingInitial = false,
  isSaving = false,
  onSave,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSelectedIds(new Set(initialSelectedIds));
    }
  }, [isOpen, initialSelectedIds]);

  const selectableAnalysts = useMemo(
    () => analystsList.filter(analyst => analyst.id && analyst.id !== ownerId),
    [analystsList, ownerId]
  );

  const filteredAnalysts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return selectableAnalysts;
    return selectableAnalysts.filter(analyst => {
      const name = analyst.name?.toLowerCase() ?? "";
      const description = analyst.description?.toLowerCase() ?? "";
      return name.includes(query) || description.includes(query);
    });
  }, [selectableAnalysts, searchQuery]);

  const toggleAnalyst = (analystId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(analystId)) {
        next.delete(analystId);
      } else {
        next.add(analystId);
      }
      return next;
    });
  };

  const handleSave = () => {
    onSave(Array.from(selectedIds));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" mx={4}>
        <ModalHeader pb={2}>
          <Text fontSize="lg" fontWeight="700" mb={0}>
            Share with analyst
          </Text>
          {workflowName && (
            <Text fontSize="sm" color="gray.500" fontWeight="500" mt={1} mb={0} noOfLines={1}>
              {workflowName}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none" h="100%">
                <Icon as={AppIcons.search} boxSize={4} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search analysts..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                borderRadius="lg"
                _focus={{
                  borderColor: "purple.400",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                }}
              />
            </InputGroup>

            <Box maxH="320px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="lg">
              {isLoadingInitial ? (
                <Flex py={10} justify="center">
                  <Spinner size="sm" color="purple.500" />
                </Flex>
              ) : filteredAnalysts.length === 0 ? (
                <Text py={8} px={4} textAlign="center" color="gray.500" fontSize="sm" mb={0}>
                  No analysts found
                </Text>
              ) : (
                <VStack spacing={0} align="stretch">
                  {filteredAnalysts.map(analyst => {
                    const isSelected = analyst.id ? selectedIds.has(analyst.id) : false;
                    return (
                      <HStack
                        key={analyst.id}
                        spacing={3}
                        px={4}
                        py={3}
                        cursor="pointer"
                        bg={isSelected ? "purple.50" : "white"}
                        _hover={{ bg: isSelected ? "purple.50" : "gray.50" }}
                        onClick={() => analyst.id && toggleAnalyst(analyst.id)}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        <Checkbox colorScheme="purple" isChecked={isSelected} pointerEvents="none" />
                        <Flex
                          align="center"
                          justify="center"
                          boxSize={9}
                          borderRadius="full"
                          bg={getAvatarColor(analyst.id ?? analyst.name ?? "")}
                          flexShrink={0}
                        >
                          <Text fontSize="xs" fontWeight="700" color="white" mb={0}>
                            {getAnalystInitials(analyst.name ?? "")}
                          </Text>
                        </Flex>
                        <Box flex={1} minW={0}>
                          <Text fontSize="sm" fontWeight="600" color="gray.800" mb={0} noOfLines={1}>
                            {analyst.name}
                          </Text>
                          {analyst.description && (
                            <Text fontSize="xs" color="gray.500" mb={0} noOfLines={1}>
                              {analyst.description}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button colorScheme="purple" onClick={handleSave} isLoading={isSaving}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ShareWorkflowModal;
